import moment from 'moment';
import * as R from 'ramda';
import * as _ from 'lodash';
import { useEffect, useRef } from 'react';
import { createNumberMask } from 'redux-form-input-masks';

import { appConfig } from '@config';
import { userTypes } from './constants';

class LocalStorageAlternative {
  constructor() {
    this.structureLocalStorage = {};
  }

  setItem(key, value) {
    this.structureLocalStorage[key] = value;
  }

  getItem(key) {
    if (typeof this.structureLocalStorage[key] !== 'undefined') {
      return this.structureLocalStorage[key];
    }
    return null;
  }

  removeItem(key) {
    this.structureLocalStorage[key] = undefined;
  }
}

let storage;

function initLocalStorageAlternative() {
  console.warn('No local storage support. Return alternative.');
  storage = new LocalStorageAlternative();
  return storage;
}

export function getLocalStorage() {
  // Check cache first.
  if (storage) {
    return storage;
  }
  try {
    // Check if the browser supports local storage.
    if (!localStorage) {
      return initLocalStorageAlternative();
    }
  } catch (e) {
    return initLocalStorageAlternative();
  }

  try {
    // If there is local storage but browser is in private mode,
    // the next lines will fail.
    localStorage.setItem('__TEST__', '');
    localStorage.removeItem('__TEST__');
    storage = localStorage;
  } catch (err) {
    storage = initLocalStorageAlternative();
  }
  return storage;
}

export function isTestEnv() {
  const url = window.location.href || '';
  return url.indexOf('localhost') > -1;
}

/**
 * Creates and returns a new debounced version of the passed function which
 * will postpone its execution until after wait milliseconds have elapsed since
 * the last time it was invoked. Useful for implementing 'search'. After we
 * stopped entering the search string, we want to perform a single search.
 * NOTE: based on underscore's debounce()
 * @param {function} func The function to be applied after a certain time.
 * @param {wait} number The number of milliseconds to wait until execution.
 */
/* eslint-disable */
export function slowdown(func, wait, immediate) {
  let timeout;
  return function () {
    const context = this;
    const args = arguments;
    var later = function () {
      timeout = null;
      if (!immediate) {
        func.apply(context, args);
      }
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) {
      func.apply(context, args);
    }
  };
}

export function isMobile() {
  if (
    navigator.userAgent.match(/Android/i) ||
    navigator.userAgent.match(/webOS/i) ||
    navigator.userAgent.match(/iPhone/i) ||
    navigator.userAgent.match(/iPad/i) ||
    navigator.userAgent.match(/iPod/i) ||
    navigator.userAgent.match(/BlackBerry/i) ||
    navigator.userAgent.match(/Windows Phone/i)
  ) {
    return true;
  } else {
    return false;
  }
}
export function getRoute(path) {
  if (path === '/') {
    return '';
  }
  return path.split('/')[1];
}

export function setVisibleColumns(columnDefs, visibleColumns) {
  if (!visibleColumns || visibleColumns.toJS().length === 0) {
    return columnDefs;
  }
  return columnDefs.map((column) => ({
    ...column,
    hide: visibleColumns.toJS().indexOf(column.field) === -1,
  }));
}

export const setVisibleColumnsR = R.curry((visibleColumns, columnDefs) =>
  setVisibleColumns(columnDefs, visibleColumns),
);

export function setLastOrderColumns(columnDefs, movedColumns, option) {
  if (!movedColumns) {
    return columnDefs;
  }
  let columns = movedColumns.toJS();

  const diffColumns = columnDefs.filter(
    (column) => columns.indexOf(column.field) === -1,
  );
  if (diffColumns.length > 0) {
    columns = columns.concat(diffColumns.map((column) => column.field));
  }
  // if (option && option.selectable) {
  //   columns = columns.filter((column) => column !== 'selectID');
  //   columns = ['selectID'].concat(columns);
  // }
  columns = columns.filter((field) =>
    columnDefs.find((column) => column.field === field),
  );
  return columns.map((key) =>
    columnDefs.find((column) => column.field === key),
  );
}

export const setLastOrderColumnsR = R.curry((movedColumns, columnDefs) =>
  setLastOrderColumns(columnDefs, movedColumns),
);

export function isFirstColumn(params) {
  let displayedColumns = params.columnApi.getAllDisplayedColumns();
  let thisIsFirstColumn = displayedColumns[0] === params.column;
  return thisIsFirstColumn;
}

export function setPinned(columnDefs, columnsPinnedState) {
  if (!columnsPinnedState) {
    return columnDefs;
  }
  const newColumnsPinnedState = columnsPinnedState.toJS();
  return columnDefs
    .filter((column) => column)
    .map((column) => ({
      ...column,
      pinned: newColumnsPinnedState[column.field]
        ? newColumnsPinnedState[column.field]
        : undefined,
    }));
}

export function setWidth(columnDefs, columnsWidth) {
  if (!columnsWidth) {
    return columnDefs;
  }
  const newColumnsWidth = columnsWidth.toJS();
  return columnDefs
    .filter((column) => column)
    .map((column) => ({
      ...column,
      width: newColumnsWidth[column.field]
        ? newColumnsWidth[column.field]
        : 200,
    }));
}

export const setWidthR = R.curry((columnsWidth, columnDefs) =>
  setWidth(columnDefs, columnsWidth),
);

export const setPinnedR = R.curry((columnsPinnedState, columnDefs) =>
  setPinned(columnDefs, columnsPinnedState),
);

export function setSortModel(columnDefs, sortModel) {
  if (sortModel.length === 0) {
    return columnDefs;
  }
  return columnDefs.map((column) => ({
    ...column,
    sort: column.field === sortModel[0].colId ? sortModel[0].sort : undefined,
  }));
}

export const setSortModelR = R.curry((sortModel, columnDefs) =>
  setSortModel(columnDefs, sortModel),
);

export function setMultiSortModel(columnDefs, sortModel) {
  if (sortModel.length === 0) {
    return columnDefs;
  }
  return columnDefs.map((column) => {
    const sortedField = sortModel.find((item) => item.colId === column.field);
    return {
      ...column,
      sort: sortedField ? sortedField.sort : undefined,
    };
  });
}

export const setMultiSortModelR = R.curry((sortModel, columnDefs) =>
  setMultiSortModel(columnDefs, sortModel),
);

export function buildCategoryLabel(category) {
  if (!category || !category.propertyUse) {
    return '';
  }
  let result = category.categoryName;
  result += ' (';
  category.propertyUse.forEach((use, index) => {
    result += use;
    if (index < category.propertyUse.length - 1) {
      result += ' | ';
    }
  });
  result += ')';
  return result;
}

export function onAfterSailsChartRender(chart) {
  const mouseDownEvent = (event) => {
    const eStart = chart.pointer.normalize(event);

    var posX = eStart.chartX;
    var posY = eStart.chartY;
    var alpha = chart.options.chart.options3d.alpha;
    var beta = chart.options.chart.options3d.beta;
    var newAlpha;
    var newBeta;
    var sensitivity = 5; // lower is more sensitive

    const mouseMoveEvent = (e1) => {
      const e = chart.pointer.normalize(e1);
      newBeta = beta + (posX - e.chartX) / sensitivity;
      chart.options.chart.options3d.beta = newBeta;
      // Run alpha
      newAlpha = alpha + (e.chartY - posY) / sensitivity;
      chart.options.chart.options3d.alpha = newAlpha;

      chart.redraw(false);
    };
    chart.container.addEventListener('mousemove', mouseMoveEvent);
    chart.container.addEventListener('mouseup', () => {
      chart.container.removeEventListener('mousemove', mouseMoveEvent);
    });
    chart.container.addEventListener('touchmove', mouseMoveEvent);
    chart.container.addEventListener('touchend', () => {
      chart.container.removeEventListener('touchend', mouseMoveEvent);
    });
  };
  chart.container.addEventListener('mousedown', mouseDownEvent);
  chart.container.addEventListener('touchstart', mouseDownEvent);
}

export function linearRegression(y, x) {
  const lr = {};
  const n = y.length;
  let sumX = 0;
  let sumY = 0;
  let sumXY = 0;
  let sumXX = 0;
  let sumYY = 0;

  for (let i = 0; i < y.length; i++) {
    sumX += x[i];
    sumY += y[i];
    sumXY += x[i] * y[i];
    sumXX += x[i] * x[i];
    sumYY += y[i] * y[i];
  }

  lr.slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
  lr.intercept = (sumY - lr.slope * sumX) / n;
  lr.r2 = Math.pow(
    (n * sumXY - sumX * sumY) /
      Math.sqrt((n * sumXX - sumX * sumX) * (n * sumYY - sumY * sumY)),
    2,
  );

  return (p) => {
    return lr.slope * p + lr.intercept;
  };
}

export function checkPageEditing(currentPath) {
  return currentPath.indexOf('-edit') !== -1;
}

export function getRootData(widgetData) {
  return widgetData
    .filter((widget) => !widget.parent)
    .map((widget) => widget.id);
}

export function getChildData(widgetData, parent) {
  return widgetData
    .filter((widget) => widget.parent === parent)
    .map((widget) => widget.id);
}

export function getItems(widgetData, id) {
  return widgetData
    .find((widget) => widget.id === id)
    .items.map((item) => item.id);
}

export function getInitialEditData(preference, widgetData, keys, rootKey) {
  const defaultEditData = {};
  defaultEditData[keys.root] = getRootData(widgetData);
  keys.children.forEach((key) => {
    defaultEditData[key] = getChildData(widgetData, key);
  });
  keys.hasItems.forEach((key) => {
    defaultEditData[key] = getItems(widgetData, key);
  });

  if (preference.edit && preference.edit[rootKey]) {
    if (preference.edit[rootKey][keys.root]) {
      defaultEditData[keys.root] = preference.edit[rootKey][keys.root];
    }
    keys.children.forEach((key) => {
      if (preference.edit[rootKey][key]) {
        defaultEditData[key] = preference.edit[rootKey][key];
      }
    });
    keys.hasItems.forEach((key) => {
      if (preference.edit[rootKey][key]) {
        defaultEditData[key] = preference.edit[rootKey][key];
      }
    });
  }
  return defaultEditData;
}

export function getType(type) {
  if (type === 'regression') {
    return 'Regression Pool';
  } else if (type === 'comp') {
    return 'Comp Selection';
  }
  return '';
}

export function getFormattedDate(date) {
  if (!date) {
    return '';
  }
  return moment(date).format('YYYY-MM-DD h:mm:ss a');
}

export function goToSystemConfiguration(routerPushWithReturnTo) {
  routerPushWithReturnTo('/equityFinder/configurations/system');
}

export function goToRuleConfiguration(routerPushWithReturnTo) {
  routerPushWithReturnTo('/equityFinder/configurations/rule');
}

export function goToCompGridConfiguration(routerPushWithReturnTo) {
  routerPushWithReturnTo('/equityFinder/configurations/compgrid');
}

export function goToBaseUnitConfiguration(routerPushWithReturnTo) {
  routerPushWithReturnTo('/equityFinder/configurations/base');
}

export function goToTemplateConfiguration(routerPushWithReturnTo) {
  routerPushWithReturnTo('/equityFinder/configurations/template');
}

export function goToProdigyScoreConfiguration(routerPushWithReturnTo) {
  routerPushWithReturnTo('/equityFinder/configurations/score');
}

export function getAbbreviated(value) {
  if (!value) return '';
  return value
    .split(' ')
    .map((item) => (item[0] ? item[0].toUpperCase() : ''))
    .join('');
}

export function hasRight(rights, right) {
  return rights.indexOf(right) !== -1;
}

export function getDistance(lat1, lon1, lat2, lon2, unit) {
  const radlat1 = (Math.PI * lat1) / 180;
  const radlat2 = (Math.PI * lat2) / 180;
  const theta = lon1 - lon2;
  const radtheta = (Math.PI * theta) / 180;
  let dist =
    Math.sin(radlat1) * Math.sin(radlat2) +
    Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);
  if (dist > 1) {
    dist = 1;
  }
  dist = Math.acos(dist);
  dist = (dist * 180) / Math.PI;
  dist = dist * 60 * 1.1515;
  if (unit == 'K') {
    dist = dist * 1.609344;
  }
  if (unit == 'N') {
    dist = dist * 0.8684;
  }
  return dist;
}

export function removeNullPropertyFromObject(data) {
  let filtered = {};

  if (data) {
    Object.keys(data).forEach((key) => {
      if (data[key]) {
        filtered[key] = data[key];
      }
    });
  }
  return filtered;
}

export function getNumberFormat(number, fractionDigits) {
  const minimumFractionDigits = fractionDigits === null ? 2 : fractionDigits;
  const formatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits,
  });
  return formatter.format(number);
}

export function getUsdFormat(number, fractionDigits) {
  const minimumFractionDigits = fractionDigits === null ? 2 : fractionDigits;
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits,
  });
  return formatter.format(number);
}

export function getCommaFormatted(number, options = {}) {
  if (number === null || number === undefined) return '';
  try {
    if (isNaN(Number(number))) return number;
    if (typeof number === 'string') {
      return Number(number).toLocaleString('en', options);
    }
    if (typeof number === 'number') {
      return number.toLocaleString('en', options);
    }
  } catch (err) {
    console.log(err, 'invalid number: ', number);
    return number;
  }
}

export function getPercentFormat(number, fractionDigits = 2) {
  if (!number) return '';
  if (typeof number !== 'string' && typeof number !== 'number') {
    return new Error('Invalid number');
  }
  const num = typeof number === 'string' ? Number(number) : number;
  return `${Number.parseFloat(Math.round(num * 100) / 100).toFixed(
    fractionDigits,
  )}%`;
}

export function getSentenceCase(string) {
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
}

export function getTitleCase(string) {
  return string
    .split(' ')
    .map((word) => getSentenceCase(word))
    .join(' ');
}

export function onlyNumbersNormalizer(value) {
  const onlyNums = value.replace ? value.replace(/[^0-9$.,]/g, '') : value;
  return onlyNums;
}

export function onlyNumbersNormalizerToNumber(value) {
  const onlyNums = value.replace ? value.replace(/[^0-9$.,]/g, '') : value;
  return Number(onlyNums);
}

export function yearNormalizer(value) {
  const onlyNums = value.replace(/[^0-9$]/g, '');
  return onlyNums.substring(0, 4);
}

export function percentNormalizer(value) {
  const onlyNums = value.replace(/[^0-9$.]/g, '');
  if (Number(onlyNums) <= 100) return onlyNums;
  return onlyNums.substring(0, 2);
}

export function timeNormalizer(value) {
  const onlyNums = value.replace(/[^0-9$.,:]/g, '');
  return onlyNums;
}

export function sqftToAcres(value) {
  return value / 43560;
}

export function acresToSqft(value) {
  return value * 43560;
}

export const numberMask = (decimals = 2) =>
  createNumberMask({
    decimalPlaces: decimals,
    locale: 'en-US',
  });

export const intNumberMask = createNumberMask({
  decimalPlaces: 0,
  locale: 'en-US',
});

export const injectScript = (src) => {
  const script = document.createElement('script');
  const lastScriptInDOM = document.getElementsByTagName('script')[0];
  script.async = 1;
  script.src = src;
  script.charset = 'UTF-8';
  lastScriptInDOM.parentNode.insertBefore(script, lastScriptInDOM);
};

export const buildApiUrl = (apiModule, endpoint = '') =>
  `https://${appConfig.environment}-${apiModule}.${appConfig.apiUrl}${endpoint}`;

export const getPortalUrl = (standardUrl, office) => {
  const urlParts = standardUrl.match(
    /^(([^:\/?#]+):)?(\/\/([^\/?#]*))?([^?#]*)(\?([^#]*))?(#(.*))?/,
  );
  const protocol = `${urlParts[1]}//`;
  const [env, ...rest] = urlParts[4].split('.');
  const domain = rest.join('.');
  if (
    domain === 'trueprodigy-portal.com' &&
    office &&
    office !== 'True Prodigy'
  ) {
    return `${protocol}${env !== 'prod' ? env + '.' : ''}${office}.${domain}`;
  } else {
    return standardUrl;
  }
};

export const usePrevious = (value) => {
  // The ref object is a generic container whose current property is mutable ...
  // ... and can hold any value, similar to an instance property on a class
  const ref = useRef();

  // Store current value in ref
  useEffect(() => {
    ref.current = value;
  }, [value]); // Only re-run if value changes

  // Return previous value (happens before update in useEffect above)
  return ref.current;
};

export const removeEmptyOptions = (searchOptions) => {
  const _searchOptions = _.cloneDeep(searchOptions);
  _.each(_searchOptions, (val, key) => {
    if (!val) {
      delete _searchOptions[key];
    } else if (
      Array.isArray(val) &&
      typeof val === 'object' &&
      val.length === 0
    ) {
      delete _searchOptions[key];
    } else if (val && typeof val === 'object' && 'value' in val && !val.value) {
      delete _searchOptions[key];
    }
  });
  return _searchOptions;
};

export const getCleanSearchOptions = (searchOptions) => {
  const _searchOptions = _.cloneDeep(searchOptions);
  _.each(_searchOptions, (val, key) => {
    if (typeof val !== 'object') {
      if (val !== false) {
        _searchOptions[key] = { operator: '=', value: val };
      } else {
        delete _searchOptions[key];
      }
    }
  });

  return _searchOptions;
};

export const decide = (options) => {
  if (!options || !options.length) return null;
  for (const option of options) {
    if (option.condition) {
      return option.return;
    }
  }
  return null;
};

export const secondsToTime = (duration) => {
  const seconds = Math.floor(duration % 60)
    .toString()
    .padStart(2, '0');
  const minutes = Math.floor((duration / 60) % 60)
    .toString()
    .padStart(2, '0');
  const hours = Math.floor((duration / (60 * 60)) % 24)
    .toString()
    .padStart(2, '0');

  return `${hours}:${minutes}:${seconds}`;
};

export const timeToSeconds = (time) => {
  const [hours, minutes, seconds] = time.split(':');
  return Number(hours) * 3600 + Number(minutes) * 60 + Number(seconds);
};

export const getSortModelForMultipleSearch = ({
  prevSortModel,
  newSortModel,
}) => {
  let currentSortModel = prevSortModel;

  const colIds = prevSortModel.map((item) => item.colId);
  const newSortModelColIds = newSortModel.map((item) => item.colId);

  const newSortItemToRemove = prevSortModel.find(
    (item) => !newSortModelColIds.includes(item.colId),
  );

  const newSortItemByColId = newSortModel.find(
    (item) => !colIds.includes(item.colId),
  );

  const newSortItemBySort = newSortModel.find(
    (item) =>
      colIds.includes(item.colId) &&
      prevSortModel.find((v) => v.colId === item.colId).sort !== item.sort,
  );

  if (newSortItemByColId) {
    currentSortModel = [...prevSortModel, newSortItemByColId];
    if (currentSortModel.length === 5) {
      currentSortModel = currentSortModel.slice(1, 5);
    }
  }

  if (newSortItemBySort) {
    currentSortModel = prevSortModel.map((item) =>
      item.colId === newSortItemBySort.colId ? newSortItemBySort : item,
    );
  }

  if (newSortItemToRemove) {
    currentSortModel = prevSortModel.filter(
      (item) => item.colId !== newSortItemToRemove.colId,
    );
  }

  return currentSortModel;
};

export const isImage = (filename) => {
  if (!filename || typeof filename !== 'string' || filename.length < 4) {
    throw new Error('Invalid filename');
  }
  const extension = filename.slice(-4);
  return ['jpeg', '.jpg', '.png', '.gif'].includes(extension.toLowerCase());
};

export const isPdf = (filename) => {
  if (!filename || typeof filename !== 'string' || filename.length < 4) {
    throw new Error('Invalid filename');
  }
  const extension = filename.slice(-4);
  return extension.toLowerCase() === '.pdf';
};

export const getEnv = () => {
  const baseUrl = window.location.hostname;
  let env = 'prod';
  if (baseUrl.includes('dev') || baseUrl.includes('localhost')) {
    env = 'dev';
  }
  if (baseUrl.includes('stage')) {
    env = 'stage';
  }
  return env;
};

export const getTaxTransparencyWebsiteLink = (office) => {
  console.log('office', office);
  const env = getEnv();
  const linkArray = [];
  let realOffice = '';

  if (env !== 'prod') {
    linkArray.push(env);
  }
  if (office.includes('PreProd')) {
    realOffice = office.split('PreProd')[0];
  } else if (office.includes('Prelim')) {
    realOffice = office.split('Prelim')[0];
  } else if (office !== 'True Prodigy') {
    realOffice = office;
  }
  if (realOffice) {
    linkArray.push(realOffice.toLowerCase());
  }
  linkArray.push('trueprodigy-taxtransparency.com');
  return `https://${linkArray.join('.')}`;
};

export const isObject = (variable) =>
  Object.prototype.toString.call(variable) === '[object Object]';

export const convertObjectToString = (obj, delimiter = ' / ') =>
  isObject(obj)
    ? Object.keys(obj)
        .reduce((str, k) => {
          // eslint-disable-next-line no-param-reassign
          return (str += `${k} - ${obj[k]}${delimiter}`);
        }, '')
        .slice(0, -delimiter.length)
    : obj;

export const getTooltipContent = (valueJs, fields) => {
  let result = [];
  if (valueJs?.pYear?.value) {
    result = result.concat([{ label: 'Year', value: valueJs.pYear.value }]);
  }
  if (valueJs?.pid?.length) {
    result = result.concat([
      { label: 'Property ID', value: valueJs.pid.join(', ') },
    ]);
  }

  for (const f of fields) {
    if (valueJs[f.id] && f.id !== 'pid') {
      if (typeof valueJs[f.id] === 'boolean') {
        result = result.concat([{ label: f.description, value: 'True' }]);
      } else {
        if (f.id === 'marketArea') {
          if (typeof valueJs[f.id] === 'string') {
            result = result.concat({
              label: f.description,
              value: valueJs[f.id],
            });
          } else if (
            valueJs[f.id].length > 0 &&
            typeof valueJs[f.id][0] === 'string'
          ) {
            result = result.concat({
              label: f.description,
              value: valueJs[f.id].join(', '),
            });
          } else if (
            valueJs[f.id].length > 0 &&
            typeof valueJs[f.id][0] === 'object'
          ) {
            result = result.concat({
              label: f.description,
              value: valueJs[f.id].map((r) => r.value).join(', '),
            });
          }
        } else if (valueJs[f.id].length === undefined) {
          // object case
          if (valueJs[f.id].operator) {
            if (valueJs[f.id].operator === 'between') {
              if (valueJs[f.id].beginValue && valueJs[f.id].endValue) {
                result = result.concat([
                  {
                    label: f.description,
                    value: `${valueJs[f.id].beginValue} ${
                      valueJs[f.id].endValue
                    } (between)`,
                  },
                ]);
              }
            } else {
              result = result.concat([
                {
                  label: f.description,
                  value: `${valueJs[f.id].value} (${valueJs[f.id].operator})`,
                },
              ]);
            }
          } else {
            result = result.concat([
              {
                label: f.description,
                value: valueJs[f.id].value,
              },
            ]);
          }
        } else if (valueJs[f.id].length > 0) {
          if (['cityCode', 'schoolCode', 'taxingUnitCode'].includes(f.id)) {
            result = result.concat({
              label: f.description,
              value: `${valueJs[f.id].map((v) => v.value).join(', ')} (${
                valueJs[`${f.id}-operator`] || f.operators[0]
              })`,
            });
          } else {
            result = result.concat({
              label: f.description,
              value: valueJs[f.id].map((v) => v.value).join(', '),
            });
          }
        }
      }
    }
  }

  return result;
};

const getAppealFieldDescription = (field) => {
  switch (field) {
    case 'appealID':
      return 'Case ID';
    case 'pIDList':
      return 'Property ID';
    case 'propertyOwner':
      return 'Owner';
    case 'appealedBy':
      return 'Appealed By';
    case 'agentID':
      return 'Agent ID';
    case 'dba':
      return 'Business Name(DBA)';
    case 'informalScheduled':
      return 'Informal Scheduled';
    case 'informalDocketID':
      return 'Informal Docket';
    case 'informalDateRange':
      return 'Informal Date';
    case 'createDateRange':
      return 'Create Date';
    case 'hearingDateRange':
      return 'Hearing Date';
    case 'pYear':
      return 'Year';
    case 'hearingScheduled':
      return 'Hearing Scheduled';
    case 'controlGroup':
      return 'Control Group';
    case 'controlGroupStatus':
      return 'Control Group Assigned';
    case 'caseAge':
      return 'Case Age';
    case 'marketValueRange':
      return 'Marget Value Range';
    case 'appealTypes':
      return 'Type';
    case 'appealStatuses':
      return 'Status Code';
    case 'appealReasons':
      return 'Reason Code';
    case 'appealAssignedTo':
      return 'Assigned To';
    case 'appealPanels':
      return 'Panel';
    case 'appealedByType':
      return 'Appealed By Type';
    case 'appealFinalized':
      return 'Appeal Finialized';
    case 'hearingTypeRequest':
      return 'Hearing Type Request';
    case 'recommendationValueSubmitted':
      return 'Recommended Value Submitted';
    case 'recommendationValueReviewed':
      return 'Recommended Value Reviewed';
    case 'recommendationValueAccepted':
      return 'Recommended Value Accepted';
    case 'taxpayerEvidence':
      return 'Taxpayer Evidence';
    case 'dueDiligenceEvidenceRequest':
      return '14-Day Evidence Requested';
    case 'cadEvidenceGenerated':
      return 'CAD Evidence Generated';
    case 'evidencePublished':
      return 'CAD Evidence Published';
    case 'autoPublishError':
      return 'Auto Publish Error';
    case 'propertyType':
      return 'Property Type';
    case 'propertySubType':
      return 'Property Sub Type';
    case 'propUseCode':
      return 'Prop Use Code';
    case 'stateCode':
      return 'State Code';
    case 'tags':
      return 'Property Tags';
    case 'county':
      return 'County';
    case 'legalDescription':
      return 'Legal Description';
    case 'taxingUnits':
      return 'Taxing Units';
    case 'exemptions':
      return 'Exemptions';
    default:
      return;
  }
};

export const getAppealTooltipContent = (valueJs) => {
  let result = [];

  for (const f of Object.keys(valueJs)) {
    if (valueJs[f]) {
      if (typeof valueJs[f] === 'boolean') {
        result = result.concat([
          { label: getAppealFieldDescription(f), value: 'True' },
        ]);
      } else if (
        typeof valueJs[f] === 'string' ||
        typeof valueJs[f] === 'number'
      ) {
        result = result.concat([
          { label: getAppealFieldDescription(f), value: valueJs[f] },
        ]);
      } else {
        if (valueJs[f]?.length === undefined) {
          // object case
          if (valueJs[f].operator) {
            if (valueJs[f].operator === 'between') {
              if (valueJs[f].beginValue && valueJs[f].endValue) {
                result = result.concat([
                  {
                    label: getAppealFieldDescription(f),
                    value: `${valueJs[f].beginValue} ${valueJs[f].endValue} (between)`,
                  },
                ]);
              }
            } else {
              result = result.concat([
                {
                  label: getAppealFieldDescription(f),
                  value: `${valueJs[f].value} (${valueJs[f].operator})`,
                },
              ]);
            }
          } else {
            result = result.concat([
              {
                label: getAppealFieldDescription(f),
                value: valueJs[f].value,
              },
            ]);
          }
        } else if (valueJs[f]?.length > 0) {
          result = result.concat([
            {
              label: getAppealFieldDescription(f),
              value: valueJs[f].map((v) => v.value || v).join(', '),
            },
          ]);
        }
      }
    }
  }

  return result;
};

export const getRedirectURL = (user, userData) => {
  let redirectUrl;

  if (user.userType === userTypes.PRIVATE) {
    redirectUrl = `https://${userData.privatePortalUrl}/reset`;
  } else if (user.userType === userTypes.PUBLIC) {
    redirectUrl = `https://${userData.publicPortalUrl}/reset-password`;
  } else {
    redirectUrl = `${window.location.origin}/reset`;
  }
  return redirectUrl;
};

export const getMessageType = (user) => {
  let messageType = user.resetPassword ? 'reset' : 'init';
  if (user.userType !== userTypes.PUBLIC) {
    messageType =
      user.loginStatus === 'pending initialization' ||
      user.loginStatus === 'needs initialization'
        ? 'init'
        : 'reset';
  }
  return messageType;
};

export const getResetBtnLabel = (user) => {
  if (user.userType === userTypes.PUBLIC) {
    return user.validated === 'Yes'
      ? 'Reset Password'
      : 'Send Validation Email';
  } else {
    if (
      user.loginStatus === 'pending initialization' ||
      user.loginStatus === 'needs initialization'
    ) {
      return 'Initialize User';
    }
    return 'Reset Password';
  }
};

export const isValidDates = (dates) => {
  const validStatus = dates.map((dt) => {
    if (dt) {
      const dtVal = new Date(dt);
      if (
        dtVal.getFullYear() < 1900 ||
        dtVal.getFullYear() > 2099 ||
        !(dtVal instanceof Date && !Number.isNaN(dtVal.valueOf()))
      ) {
        return false;
      }
    }
    return true;
  });
  return !validStatus.includes(false);
};
